import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuid } from "uuid";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import ImageList from "@mui/material/ImageList";
import IconButton from "@mui/material/IconButton";
import MuiAlert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import Editor from "components/elements/Editor";
import AssignmentImage from "components/elements/AssignmentImage";

import rpc, { buildRequest, signoutAndRedirectToIndex } from "lib/rpc";

const AlertM = React.forwardRef(function AlertM(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function OpenQuestionAssignmentForm(props) {
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [assignmentLevel, setAssignmentLevel] = useState("a0");

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles(
            acceptedFiles.map((file) => {
              const filename = `${uuid()}.${file.name.split(".").pop()}`;
              return Object.assign(file, {
                filename: filename,
                preview: URL.createObjectURL(file),
                base64Image: e.target.result,
                link: `https://cdn.icepig.online/media/assignments/${filename}`,
              });
            }),
          );
        };
        reader.readAsDataURL(file);
      });
    },
  });

  useEffect(() => {
    if (content === null) {
      setContent("");
    }
  }, [content]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const action = (
    <React.Fragment>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  const removeFile = (index) => {
    URL.revokeObjectURL(files[index].preview);
    setFiles(files.filter((file, i) => i !== index));
  };

  const thumbs = files.map((file, index) => (
    <AssignmentImage preview={file.preview} link={file.link} index={index} removeFile={removeFile} />
  ));

  const submitForm = (e) => {
    e.preventDefault();
    if (name.length < 1 || content.length < 1) {
      return false;
    }

    const yamlContent = `version: 4
type: openquestion
name: ${name}
level: ${assignmentLevel}
content: |

${content
  .split("\n")
  .map((line) => `  ${line}`)
  .join("\n")}

`;
    rpc
      .post(
        "",
        buildRequest("admin.assignments.create_openquestion_attach", {
          name,
          lesson_id: props.lessonId,
          source_yaml: yamlContent,
          files: files.map((file) => ({
            name: file.filename,
            base64: file.base64Image,
          })),
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          if (response.data.result.success) {
            setMessage(`OpenQuestion [${name}] has been created and attached to the Lesson [${props.lessonId}]`);
            setName("");
            setContent("");
            files.forEach((file, i) => removeFile(i));
            props.refreshLesson(props.lessonId);
            setOpen(true);
          } else {
            setMessage("Something went wrong");
            setOpen(true);
          }
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          setMessage(response.data.result.message);
          console.error(response.data.result);
          setOpen(true);
        }
      });
    return false;
  };

  if (props.lessonId) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="overline" gutterBottom>
              Create OpenQuestion and attach it to the Lesson
            </Typography>
            <Alert severity="info">
              Це тип завдання, який потребує ручної перевірки від викладача. Для зручності студента першу перевірку
              відразу після відправки виконує ChatGPT. Саме тому дуже важливо не ригнути завдання типу "Напишіть 10
              речень про кота", а включити також інформацію для перевірки. Наприклад, "Напишіть 10 речень про кота,
              використовуючи Past Perfect Continuous та Passive Voice, використайте слова: help, me, I, do, this, for,
              free". Таким чином ChatGPT буде знати деталі завдання і зможе перевірити його виконання і дати толкові
              рекомендації, а не загальне "Sentence is correct", коли людина рівня B2 пукнула речення рівня
              дошкільнятка. Ви можете описувати це завдання як українською, так і англійською мовою, or взагалі
              змішувати them if you know шо я маю на увазі.
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <form onSubmit={submitForm} method="post">
              <Stack direction="column" spacing={2}>
                <TextField
                  type="text"
                  id="theory-name"
                  label="Name"
                  variant="standard"
                  value={name ? name : ""}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  required
                />
                <Select
                  labelId="level-select-label"
                  id="level-select"
                  value={assignmentLevel}
                  label="Level"
                  onChange={(e) => setAssignmentLevel(e.target.value)}
                >
                  <MenuItem value={"a0"}>A0</MenuItem>
                  <MenuItem value={"a1"}>A1</MenuItem>
                  <MenuItem value={"a1plus"}>A1+</MenuItem>
                  <MenuItem value={"a2"}>A2</MenuItem>
                  <MenuItem value={"b1"}>B1</MenuItem>
                  <MenuItem value={"b2"}>B2</MenuItem>
                  <MenuItem value={"c1"}>C1</MenuItem>
                </Select>
                <Typography variant="body1" gutterBottom>
                  version: 2<br />
                  type: theory
                  <br />
                  name: {name}
                  <br />
                  level: {assignmentLevel}
                  <br />
                  content: |<br />
                  <br />
                </Typography>
                {content !== null && <Editor defaultValue={content || ""} value={content} setValue={setContent} />}

                <div>
                  <div {...getRootProps({ className: "dropzone" })}>
                    <input {...getInputProps()} />
                    <p>Drag 'n' drop some files here, or click to select files</p>
                  </div>
                  <ImageList>{thumbs}</ImageList>
                </div>

                <Button type="submit" variant="contained" onClick={submitForm} disabled={!name || !content}>
                  Create and attach
                </Button>
              </Stack>
            </form>
          </Grid>
        </Grid>

        <Snackbar
          open={open}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          autoHideDuration={2000}
          onClose={handleClose}
          action={action}
        >
          <AlertM onClose={handleClose} severity="success" sx={{ width: "100%" }}>
            {message}
          </AlertM>
        </Snackbar>
      </Box>
    );
  } else {
    return (
      <Stack spacing={1}>
        {/* For variant="text", adjust the height via font-size */}
        <Skeleton variant="text" sx={{ fontSize: "1.1rem" }} />
        {/* For other variants, adjust the size with `width` and `height` */}
        <Stack direction="row" spacing={2}>
          <Skeleton variant="rectangular" width={200} height={40} />
          <Skeleton variant="rectangular" width={200} height={40} />
        </Stack>
      </Stack>
    );
  }
}
